import {
	HttpClient,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpHeaders,
	HttpInterceptor,
	HttpParameterCodec,
	HttpParams,
	HttpRequest,
	HttpResponse,
} from "@angular/common/http";
import {catchError, shareReplay} from "rxjs/operators";
import {firstValueFrom, Observable, of, throwError} from "rxjs";
import {CustomHttpParameterCodec} from "./custom-http-parameter-codec";
import {inject, Injectable, Injector} from "@angular/core";
//THIS HAS TO BE AN IMPORT FROM "../core/services/sim-services/sim-local-storage.service" and not SHORTENED FORM!!!
// noinspection ES6PreferShortImport
import {SimClientStorageService} from "../core/services/sim-services/sim-client-storage.service";
// noinspection ES6PreferShortImport
// noinspection ES6PreferShortImport
import {SimInjectableDataService} from "../core/services/sim-services/sim-injectable-data.service";
import {ApiError} from "@models";
import {NotificationService} from "@common/services/notification.service";

/**
 * ApiServiceBase
 *
 * providing basic Service functionality for HttpServices
 */
export abstract class ApiServiceBase {
	//HttpContextExtensions.WebSocketIdHeaderName
	public static readonly WEBSOCKET_ID_KEY = "web-socket-id";
	public static readonly REFERRAL_RECORDER_ID = "referral-recorder-id";
	public static readonly MAC_KEY = "mac";
	public static readonly DEVICE_ID_KEY = "device-id";
	public static readonly DEVICE_NAME_KEY = "device-name";

	protected readonly _http: HttpClient;
	private readonly clientStorageService: SimClientStorageService;
	private readonly injectableDataService: SimInjectableDataService;

	/**
	 * @param injector
	 */
	protected constructor(readonly injector: Injector) {
		this.encoder = new CustomHttpParameterCodec();
		this._http = injector.get(HttpClient);
		this.clientStorageService = injector.get(SimClientStorageService);
		this.injectableDataService = injector.get(SimInjectableDataService);
	}

	protected readonly encoder: HttpParameterCodec;
	readonly loadingBarsDisabled: boolean = false;

	/**
	 * Initiates a get request

	 * @param path path of the api method in the web api controller
	 * @param queryParams the parameters that should be passed in the query string
	 * @param queryParamsAsJson array of the keys in parameters that should be passed as json in the query string
	 */
	protected get<T = any>(path: string, queryParams?: object, queryParamsAsJson?: string[]) {
		//console.log(path);
		return this.handleHttpObservableAsPromise(
			this._http.get<T>(path, {
				params: this.createHttpParams(queryParams, queryParamsAsJson),
				headers: this.createHttpHeaders(),
			}),
		);
	}

	/**
	 * Returns the Url a Get Request would trigger

	 * @param path path of the api method in the web api controller
	 * @param queryParams the parameters that should be passed in the query string
	 * @param queryParamsAsJson array of the keys in parameters that should be passed as json in the query string
	 */
	protected getUrl(path: string, queryParams?: object, queryParamsAsJson?: string[]): string {
		this.handleHttpObservableAsPromise(
			this._http.get(path, {
				params: this.createHttpParams(queryParams, queryParamsAsJson),
				headers: new HttpHeaders({url_only: "true"}),
			}),
		);

		//This works because javascript is entirely single threaded
		const url = SimHttpInterceptor.lastUrl;
		if (!url.startsWith(path)) {
			console.error(`getUrl failed! expected start: ${path}, got ${url}`);
		}
		return url;
	}

	/**
	 * Initiates a post request

	 * @param path path of the api method in the web api controller
	 * @param body data that should be passed in the body
	 * @param queryParams the parameters that should be passed in the query string
	 * @param queryParamsAsJson array of the keys in parameters that should be passed as json in the query string
	 */
	protected post<T = any>(path: string, body: any, queryParams?: object, queryParamsAsJson?: string[]) {
		//console.log(path);

		return this.handleHttpObservableAsPromise(
			this._http.post<T>(path, body, {
				params: this.createHttpParams(queryParams, queryParamsAsJson),
				headers: this.createHttpHeaders(),
				reportProgress: false,
				observe: "body",
				withCredentials: true,
			}),
		);
	}

	/**
	 * Initiates a post request with progress

	 * @param path path of the api method in the web api controller
	 * @param body data that should be passed in the body
	 * @param queryParams the parameters that should be passed in the query string
	 * @param queryParamsAsJson array of the keys in parameters that should be passed as json in the query string
	 * @param observe
	 * @param reportProgress
	 */
	protected upload<T = any>(
		path: string,
		body: FormData,
		queryParams?: object,
		queryParamsAsJson?: string[],
		observe?: "body",
		reportProgress?: boolean,
	): Observable<T>;
	protected upload<T = any>(
		path: string,
		body: FormData,
		queryParams?: object,
		queryParamsAsJson?: string[],
		observe?: "events",
		reportProgress?: boolean,
	): Observable<HttpEvent<T>>;
	protected upload<T = any>(
		path: string,
		body: FormData,
		queryParams?: object,
		queryParamsAsJson?: string[],
		observe: "body" | "events" = "body",
		reportProgress: boolean = false,
	): Observable<any> {
		//console.log(path);

		return this.handleHttpObservable(
			this._http.post<T>(path, body, {
				params: this.createHttpParams(queryParams, queryParamsAsJson),
				headers: this.createHttpHeaders(),
				reportProgress: reportProgress,
				observe: <any>observe,
				withCredentials: true,
			}),
			true,
		);
	}

	protected createFormData(parameters: object): FormData {
		let formData = new FormData();

		if (parameters) {
			Object.entries(parameters).forEach(([key, value]) => {
				if (value instanceof File) {
					formData.append(key, value, value.name);
				} else {
					let strValue: string;
					if (value instanceof Date) {
						strValue = JSON.stringify(value.toISOString());
					} else {
						strValue = JSON.stringify(value);
					}
					formData.append(key, strValue);
				}
			});
		}

		return formData;
	}

	private createHttpParams(parameters: object | undefined, parametersAsJson: string[] | undefined): HttpParams {
		parameters = parameters || {};
		parametersAsJson = parametersAsJson || [];

		let params = new HttpParams({encoder: this.encoder});

		if (parameters) {
			Object.entries(parameters).forEach(([key, value]) => {
				if (parametersAsJson?.includes(key)) {
					params = params.append(key, JSON.stringify(value));
				} else if (value instanceof Date) {
					params = params.append(key, value.toISOString());
				} else if (value instanceof Array) {
					value.forEach((item) => (params = params.append(`${key}`, item)));
				} else {
					params = params.append(key, value ?? "");
				}
			});
		}
		return params;
	}

	private createHttpHeaders(): HttpHeaders {
		const headers = new Headers();

		if (this.loadingBarsDisabled) {
			headers.append("ignoreLoadingBar", "");
		}

		headers.append(ApiServiceBase.WEBSOCKET_ID_KEY, this.clientStorageService.getData(ApiServiceBase.WEBSOCKET_ID_KEY) || "");
		headers.append(ApiServiceBase.REFERRAL_RECORDER_ID, this.clientStorageService.getData(ApiServiceBase.REFERRAL_RECORDER_ID) || "");
		headers.append(ApiServiceBase.MAC_KEY, this.injectableDataService?.mac || "");
		headers.append(ApiServiceBase.DEVICE_ID_KEY, this.injectableDataService?.clientData?.deviceId || "");
		headers.append(ApiServiceBase.DEVICE_NAME_KEY, this.injectableDataService?.hostData.hostname || "");

		return new HttpHeaders(headers);
	}

	private handleHttpObservableAsPromise<T>(observable: Observable<T>): Promise<T> {
		return firstValueFrom(this.handleHttpObservable(observable));
	}

	private handleHttpObservable<T>(observable: Observable<T>, pure: boolean = false) {
		if (!pure) {
			return observable;
		}

		const result = observable.pipe(shareReplay(1));
		//we have to subscribe here, because observables will never execute if not subscribed
		result.subscribe();
		return result;
	}
}

@Injectable()
export class SimHttpInterceptor implements HttpInterceptor {
	public static lastUrl: string;
	readonly #notificationService = inject(NotificationService);

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (req.headers.get("url_only")) {
			SimHttpInterceptor.lastUrl = req.urlWithParams;
			return of(new HttpResponse({body: req.urlWithParams}));
		}

		return next.handle(req).pipe(catchError(this._handleServerError()));
	}

	private _handleServerError(): (error: HttpErrorResponse) => Observable<never> {
		return (error: HttpErrorResponse) => {
			const apiError = new ApiError(error);

			if (apiError.hasSimError()) {
				console.group("Server error occurred at: " + new Date().toTimeString());
				console.error(apiError.simError.message);
				if (apiError.simError.additionalData?.length) {
					console.table(apiError.simError.additionalData);
				}
				console.groupEnd();
				if (apiError.simError.translationKey) {
					apiError.wasLogged = true;

					this.#notificationService.notifyError(apiError.simError.translationKey, apiError.simError.translationArgs);
				}
			}
			return throwError(() => apiError);
		};
	}
}
